import { useEffect, useId, useRef, useState } from 'react';
import type { ReactNode } from 'react';

/**
 * Horizontal-scroll containment for a wide table.
 *
 * Pre-P8 punch list, Tier 2 (mobile): the Dashboard's Employees table and
 * the Admin Console's Roster / Managers / Locations tables dragged the WHOLE
 * page into horizontal scroll on a phone — the header, the cards, everything
 * slid together. Now the table sits in a box that scrolls on its own; the
 * page never does. `minWidth` keeps the columns at readable widths inside
 * that box instead of letting six of them crush into a 375px viewport.
 *
 * The box only BEHAVES like a scroll region when it actually overflows —
 * measured (scrollWidth > clientWidth) and re-measured whenever the box or
 * its content changes size. Then it becomes a focusable region named by the
 * table's own <caption> (so a keyboard user can Tab to it and scroll with
 * the arrow keys) and a one-line "scroll sideways" hint appears, because
 * mobile browsers hide scrollbars and a cut-off table otherwise looks
 * complete. When the table fits, none of that renders: no dead Tab stop, no
 * hint begging you to swipe a table that is all there. The first draft keyed
 * both on viewport width alone; four reviewers independently showed that
 * lies in both directions (the Dashboard's table fit at 736px and still said
 * "swipe"; the 44rem admin tables overflow a 768px card with no hint at all).
 *
 * The hint is real text, not aria-hidden: a low-vision reader using a
 * screen reader with zoom needs the same instruction sighted users get. Only
 * the arrow glyph is hidden from assistive tech.
 *
 * The focus ring is drawn INSIDE the box (outline-offset) so overflow:auto
 * cannot clip it, and the box carries a couple of pixels of side padding
 * (pulled back with a matching negative margin, so nothing shifts) so the
 * inset ring lands in padding rather than over the first column's text —
 * the Dashboard's cells have no left padding of their own.
 *
 * Stacked "card" views below 768px were considered and deferred: four more
 * render paths for four tables, versus one container — the containment fixes
 * the page-scroll bug with a fraction of the surface. Recorded as a follow-up.
 */
export function TableScroller({
  labelledBy,
  minWidth,
  children,
}: {
  /** id of the table's <caption> (visually hidden is fine) — names the scroll region. */
  labelledBy: string;
  /** CSS length the table keeps at minimum, e.g. '44rem'. */
  minWidth: string;
  children: ReactNode;
}) {
  const boxRef = useRef<HTMLDivElement | null>(null);
  const [overflows, setOverflows] = useState(false);
  const hintId = useId();

  useEffect(() => {
    const box = boxRef.current;
    if (!box) return;
    const measure = () => setOverflows(box.scrollWidth > box.clientWidth + 1);
    measure();
    // Re-measure when the box or its content changes size (a rotated phone,
    // an expanded history row, a longer name after an edit).
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }
    const ro = new ResizeObserver(measure);
    ro.observe(box);
    if (box.firstElementChild) ro.observe(box.firstElementChild);
    return () => ro.disconnect();
  }, []);

  return (
    <div>
      {overflows && (
        <p id={hintId} style={S.hint}>
          Swipe or scroll sideways to see every column
          <span aria-hidden="true"> →</span>
        </p>
      )}
      <div
        ref={boxRef}
        role={overflows ? 'region' : undefined}
        aria-labelledby={overflows ? labelledBy : undefined}
        aria-describedby={overflows ? hintId : undefined}
        tabIndex={overflows ? 0 : undefined}
        style={S.box}
      >
        <div style={{ minWidth }}>{children}</div>
      </div>
    </div>
  );
}

const S = {
  box: {
    overflowX: 'auto',
    WebkitOverflowScrolling: 'touch',
    // Draw the focus ring inside the scrolling box, not clipped by it …
    outlineOffset: '-2px',
    // … and give it padding to land in, so it never sits over the first or
    // last column's text. The negative margin keeps the table aligned.
    padding: '2px 2px 0.25rem',
    margin: '0 -2px',
  },
  hint: {
    margin: '0 0 0.4rem',
    fontSize: '0.75rem',
    color: '#5f6b7a',
    textAlign: 'right',
  },
} satisfies Record<string, React.CSSProperties>;
